<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="loading"
            :query-changed="$_queryHandler_hasQueryChanged"
            :query-count="$_queryHandler_numberOfQueries"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" clearable hide-details />
            </template>

            <template #default>
              <v-row>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.site_group_id"
                    :disabled="loading"
                    :items="siteGroupOptions"
                    label="Site Group"
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.site_id"
                    :disabled="loading"
                    :items="siteFilters"
                    label="Site"
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.page_type_id"
                    :disabled="loading"
                    :items="pageTypeFilters"
                    label="Page Type"
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.language_id"
                    :disabled="loading"
                    :items="languageFilters"
                    label="Language"
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.wp_publish_status"
                    :disabled="loading"
                    :items="wpPublishStatusFilters"
                    label="Publish Status"
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.author_id"
                    :disabled="loading"
                    :items="authors"
                    custom-type="avatar"
                    label="Author"
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.is_revised"
                    :disabled="loading"
                    :items="revisionOptions"
                    label="Contains Revisions"
                    hide-details
                    clearable
                  />
                </v-col>
              </v-row>
            </template>
            <template #content>
              <pages-table
                :items="pages"
                disable-sort
                :items-per-page="pagination.perPage"
                :page="pagination.currentPage"
                :server-items-length="pagination.total"
                :footer-props="{ 'items-per-page-options': perPageOptions }"
                @pagination="$_queryHandler_onUpdatePagination"
              />
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <page-create-modal v-model="showCreateModal" @cancel="toggleCreateModal" />
    <mass-meta-export-modal
      v-model="showExportModal"
      :site-filters="siteFilters"
      :language-filters="languageFilters"
      :page-type-filters="pageTypeFilters"
      @cancel="toggleExportModal"
    />
    <mass-meta-import-modal v-model="showImportModal" @cancel="toggleImportModal" />

    <fab-actions
      :can-edit="isPermittedTo('update')"
      context="Page"
      @create="toggleCreateModal"
      @export="toggleExportModal"
      @import="toggleImportModal"
    />
  </page-loader>
</template>

<script>
// Helpers
import { mapActions, mapGetters, mapMutations } from 'vuex'

// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'

// Services
import { authors, pages, siteGroups } from '@/services/offers'

// Components
import FabActions from '@/components/fab-actions'
import FilterAccordion from '@/components/filter-accordion'
import MassMetaExportModal from '@/views/content/pages/components/mass-meta-export-modal'
import MassMetaImportModal from '@/views/content/pages/components/mass-meta-import-modal'
import PageCreateModal from '@/views/content/pages/components/create-modal'
import PageLoader from '@/components/page-loader'
import PagesTable from './components/pages-table'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'

export default {
  name: 'PagesIndex',
  components: {
    FabActions,
    FilterAccordion,
    MassMetaExportModal,
    MassMetaImportModal,
    PageCreateModal,
    PageLoader,
    PagesTable,
    SearchInput,
    SelectList,
  },
  mixins: [PermissionsHandler, QueryParamsHandler],
  data() {
    return {
      authors: [],
      loading: false,
      showCreateModal: false,
      showImportModal: false,
      showExportModal: false,
      queriesDefined: [
        'author_id',
        'page_type_id',
        'site_id',
        'site_group_id',
        'language_id',
        'use_legacy_editor',
        'seo_approved',
        'wp_publish_status',
        'searchTerm',
        'is_revised',
      ],
      queryTypes: { is_revised: 'number', page_type_id: 'number' },
      pages: [],
      pagination: {
        perPage: 50,
      },
      perPageOptions: [15, 50, 100, 200],
      revisionOptions: [
        { key: 'Yes', value: 1 },
        { key: 'No', value: 0 },
      ],
      siteGroupOptions: [],
    }
  },
  computed: {
    ...mapGetters({
      getItemsForOption: 'options/getItemsForOption',
      languages: 'languages/getLanguages',
      pageTypes: 'pageTypes/getPageTypes',
      sites: 'sites/getSites',
    }),
    hasEnabledFilters() {
      return !!Object.keys(this.query.params).length
    },
    siteFilters() {
      return this.sites.data.map(({ id: value, hostname: key }) => ({ key, value }))
    },
    siteGroupSiteIds() {
      return this.sites.data
        .filter(({ site_group_id }) => site_group_id === this.query.params.site_group_id)
        .map(({ id }) => id)
    },
    languageFilters() {
      return this.languages.data.map(({ id: value, name: key }) => ({ key, value }))
    },
    pageTypeFilters() {
      return this.pageTypes.data.map(({ id: value, name: key }) => ({ key, value }))
    },
    wpPublishStatusFilters() {
      return this.getItemsForOption('wordpressStatuses')
    },
  },
  async created() {
    const paginate = { perPage: null }
    await Promise.all([
      this.fetchSites({ paginate }),
      this.fetchLanguages({ paginate }),
      this.fetchPageTypes({ paginate }),
      this.fetchAuthors(),
      this.fetchSiteGroups(),
    ])
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations({
      setPageLoading: 'general/setPageLoading',
    }),
    ...mapActions({
      fetchSites: 'sites/fetch',
      fetchLanguages: 'languages/fetch',
      fetchPageTypes: 'pageTypes/fetch',
    }),
    async fetchAuthors() {
      this.isLoading = true
      try {
        const { data } = await authors.getAll({ paginate: { perPage: null } })
        this.authors = data.map(({ first_name, last_name, id, profile_picture }) => ({
          key: `${first_name} ${last_name}`,
          value: id,
          icon: profile_picture,
        }))
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch authors.',
          data: { error },
        })
      }
      this.isLoading = false
    },
    async fetchPages() {
      this.loading = true
      const { currentPage: page, perPage } = this.pagination

      const { site_group_id } = this.query.params
      const filters = Object.fromEntries(
        [
          ...Object.entries(this.query.params).filter(([key, value]) => key !== 'site_group_id'),
          ...[site_group_id ? ['site_idIn', this.siteGroupSiteIds] : null],
        ].filter(Boolean)
      )

      const {
        data,
        pagination: { total },
      } = await pages
        .getAll({
          search: this.query.searchTerm,
          paginate: { perPage, page },
          filters,
        })
        .catch(() => this.showErrorMessage())

      this.pages = data
      this.pagination.total = total
      this.loading = false
    },
    async fetchSiteGroups() {
      this.isLoading = true
      try {
        const { data } = await siteGroups.getAll({ paginate: { perPage: null } })
        this.siteGroupOptions = data.map(({ id, name }) => ({ key: name, value: id }))
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch site groups',
          data: { error },
          duration: -1,
        })
      }
      this.isLoading = false
    },
    openInNewTab(url) {
      window.open(url, '_blank').focus()
    },
    showErrorMessage(text = 'Unable to retrieve pages') {
      this.$notify({ type: 'error', text })
    },
    toggleCreateModal() {
      this.showCreateModal = !this.showCreateModal
    },
    handleQueryUpdate() {
      this.fetchPages()
    },
    toggleImportModal() {
      this.showImportModal = !this.showImportModal
    },
    toggleExportModal() {
      this.showExportModal = !this.showExportModal
    },
  },
}
</script>
